import { useEffect, useMemo, useRef } from "react";
import { advanceCharge, flyOffDurationMs } from "../lib/spark.js";

export interface SparkState {
  charge: number;
  holding: boolean;
  /** `performance.now()` when the spark took off, null while it sits in the centre. */
  flightStart: number | null;
  /** `performance.now()` when the spark (re)appeared, for the fade-in; Infinity until it first appears. */
  appearStart: number;
}

type FrameListener = (state: Readonly<SparkState>, now: number) => void;

export interface SparkController {
  state: Readonly<SparkState>;
  setHolding(holding: boolean): void;
  /** Called every animation frame; returns an unsubscribe. */
  onFrame(listener: FrameListener): () => void;
  /** Fades the spark in; it starts hidden. */
  appear(): void;
  /** Starts the flight; resolves once the spark is off-screen and its trail has faded. */
  launch(): Promise<void>;
  /** Puts an uncharged spark back in the centre. */
  reset(): void;
}

/**
 * One animation loop shared by the canvas and the hold button.
 *
 * State lives in a ref rather than React state, so a 60 fps charge doesn't re-render
 * the page; consumers read it in their frame listeners.
 */
export function useSpark(): SparkController {
  const stateRef = useRef<SparkState>({
    charge: 0,
    holding: false,
    flightStart: null,
    appearStart: Number.POSITIVE_INFINITY,
  });
  const listeners = useRef(new Set<FrameListener>());

  useEffect(() => {
    let frame = 0;
    let last = performance.now();
    const tick = (now: number): void => {
      const state = stateRef.current;
      // Capped so a backgrounded tab doesn't come back fully charged.
      const dt = Math.min(now - last, 100);
      last = now;
      if (state.flightStart === null) state.charge = advanceCharge(state.charge, dt, state.holding);
      for (const listener of listeners.current) listener(state, now);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return useMemo<SparkController>(
    () => ({
      state: stateRef.current,
      setHolding(holding) {
        stateRef.current.holding = holding;
      },
      onFrame(listener) {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
      },
      appear() {
        stateRef.current.appearStart = performance.now();
      },
      launch() {
        const state = stateRef.current;
        state.holding = false;
        // Also when launched from the tweak panel without holding.
        state.charge = 1;
        state.flightStart = performance.now();
        // Waits for the trail and embers too, so leaving the page doesn't cut it off mid-fade.
        return new Promise((resolve) => setTimeout(resolve, flyOffDurationMs()));
      },
      reset() {
        const state = stateRef.current;
        state.charge = 0;
        state.holding = false;
        state.flightStart = null;
        state.appearStart = performance.now();
      },
    }),
    [],
  );
}
